// ============================================================================
// COMPONENTE: REGISTROS DUPLICADOS DJ1945
// Gestiona la visualización y corrección de registros duplicados en declaraciones
// ============================================================================

import { Component, OnInit, OnDestroy, Input, Output, EventEmitter } from '@angular/core';
import pageSettings from '../../../config/page-settings';
import { EntidadService } from '../../../services/entidad.service';
import Swal from 'sweetalert2'
import { DeclaracionService } from '../../../services/declaracion.service';
import { RegistroDuplicado } from '../../../models/dj1945/registro_duplicado';
import { Subscription } from "rxjs"
import { ModalDismissReasons, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { DjConfiguracionGlobalPeriodo } from '../../../models/dj1945/dj_configuracion_global_periodo.model';
import { DjDuplicateRecord, DjDuplicateRecordResponse } from '../../../models/dj1945/duplicate_record/dj_duplicate_record.model';
import { DjOptionDuplicateRecordUpdateRequest, DjOptionDuplicateRecordUpdateResponse } from '../../../models/dj1945/duplicate_record/dj_option_duplicate_record.model';
import { DjAutoCorrectionDuplicateRecordRequest, DjAutoCorrectionDuplicateRecordResponse } from '../../../models/dj1945/duplicate_record/dj_auto_correction_duplicate_record.model';
import { DjRowResponse } from '../../../models/dj1945/dj_row_response.model';
import { SharingService } from '../../../services/sharing.service';

@Component({
  selector: 'app-registros-duplicados',
  templateUrl: './registros-duplicados.component.html',
  styleUrls: ['./registros-duplicados.component.css'],
  providers: [EntidadService, DeclaracionService, NgbModal]
})
export class RegistrosDuplicadosComponent implements OnInit, OnDestroy {

  // #region PROPIEDADES INPUT/OUTPUT
  @Input() mostrarComoSubcomponente: boolean = false;
  @Output() volverADeclaracion = new EventEmitter<void>();
  // #endregion

  pageSettings = pageSettings;
  
  // #region PROPIEDADES UI
  public titulo = "Registros Duplicados";
  public descripcion = "DJ1945";
  public icono = "fas fa-clone fa-fw";
  public maxTableHeight: string = '60vh'
  public isLoading: boolean = false;
  public closeResult: string
  // #endregion

  // #region PROPIEDADES DATOS
  public registros_duplicados: Array<RegistroDuplicado>
  public opcion_duplicado: Array<DjOptionDuplicateRecordUpdateRequest>;
  public dj_config: DjConfiguracionGlobalPeriodo
  public dj_duplicate_records: Array<DjDuplicateRecord>
  public response_params: any
  public entity = this._entidadService.getEntidad();
  private entidadActual: any; // Agregar para tracking de entidad actual
  private scrollbarWidth: number = 0;
  public entidadSelSubscription: Subscription;
  // #endregion

  constructor(
    private _entidadService: EntidadService,
    private _declaracionService: DeclaracionService,
    private _sharingService: SharingService,
    private modalService: NgbModal
  ) { 
    this.calculateScrollbarWidth();
    this.entidadActual = this._entidadService.getEntidad(); // Inicializar entidad actual
  }

  async ngOnInit(): Promise<void> {
    // Solo suscribirse si no es un subcomponente
    if (!this.mostrarComoSubcomponente) {
      this.entidadSelSubscription = this._sharingService.sharingEntity.subscribe(
        () => this.getConfiguracionGlobalPeriodo()
      )
    } else {
      // Si es subcomponente, suscribirse al cambio de entidad para volver a declaración
      this.entidadSelSubscription = this._sharingService.sharingEntity.subscribe(
        (nuevaEntidad) => {
          // Solo volver si realmente cambió la entidad
          if (this.entidadActual && nuevaEntidad && 
              (this.entidadActual as any)?._id !== (nuevaEntidad as any)?._id) {
            //console.log('Cambio de entidad detectado en registros duplicados, volviendo a declaración...');
            setTimeout(() => {
              this.volverADeclaraciones();
            }, 0);
          }
          // Actualizar la entidad actual
          this.entidadActual = nuevaEntidad;
        }
      )
      // Cargar datos directamente para el estado inicial
      this.getConfiguracionGlobalPeriodo()
    }
    this.calculateTableHeight()
  }

  // #region MÉTODOS TABLA
  trackByRecord(index: number, record: any): any {
    return record.rut || index;
  }

  calculateTableHeight(): void {
    const screenHeight = window.innerHeight
    this.maxTableHeight = screenHeight < 768 ? '50vh' : screenHeight < 1024 ? '55vh' : '65vh'
  }

  private calculateScrollbarWidth(): void {
    const outer = document.createElement('div');
    outer.style.cssText = 'visibility:hidden;overflow:scroll';
    document.body.appendChild(outer);
    const inner = document.createElement('div');
    outer.appendChild(inner);
    this.scrollbarWidth = outer.offsetWidth - inner.offsetWidth;
    document.body.removeChild(outer);
  }

  getScrollbarCompensation(): string {
    return this.scrollbarWidth > 0 ? `calc(100% - ${this.scrollbarWidth}px)` : '100%';
  }

  onResize() {
    this.calculateTableHeight()
    this.calculateScrollbarWidth()
  }
  // #endregion

  // #region MÉTODOS MODAL
  open(content, record) {
    this.elegirRegistrosDuplicados(record)
    this.modalService.open(content, { windowClass: "modal-holder", size: "md", centered: true })
      .result.then(
        (result) => this.closeResult = `Closed with: ${result}`,
        (reason) => this.closeResult = `Dismissed ${this.getDismissReason(reason)}`
      );
  }

  private getDismissReason(reason: any): string {
    if (reason === ModalDismissReasons.ESC) return 'by pressing ESC';
    if (reason === ModalDismissReasons.BACKDROP_CLICK) return 'by clicking on a backdrop';
    return `with: ${reason}`;
  }

  closeModal() { this.modalService.dismissAll(); }
  // #endregion

  // #region LÓGICA NEGOCIO
  elegirRegistrosDuplicados(record) {
    
    // Verificar si record.records existe, si no, usar el arreglo que esté disponible
    const recordsArray = record.records || record.registros_duplicados || [];
    
    if (!recordsArray || recordsArray.length === 0) {
      console.warn('No se encontraron registros duplicados para este elemento');
      this.opcion_duplicado = [];
      return;
    }
    
    this.opcion_duplicado = recordsArray.map(x => {
      x.record = record;
      return x;
    });
    
  }

  agruparRegistro(od) {
    this.showConfirmation('Advertencia', '¿Está Seguro de Agrupar estos Registros?', 'Sí, Agrupar', () => {
      const option_duplicate_record: DjOptionDuplicateRecordUpdateRequest = {
        rut: od.record.rut,
        tipo_ing_egr: od.record.tipo_ing_egr,
        pais: od.record.pais,
        request: {
          origen_ing: od.origen_ing,
          destino_egr: od.destino_egr,
          tipo_relacion: od.tipo_relacion,
          codigo_entidad: od.record.codigo_entidad
        }
      }
      this.updateDuplicateRecord(option_duplicate_record)
    })
  }

  autocorrection() {
    this.showConfirmation('Advertencia', 
      'Esta Acción corregirá las declaraciones tomando en cuenta periodos anteriores ¿Está Seguro de Realizar esta Acción?', 
      'Sí, Corregir', () => {
        Swal.fire({
          title: "Autocorrección",
          text: "Se están corrigiendo los registros tomando en cuenta los periodos anteriores...",
          allowOutsideClick: false,
          timerProgressBar: true,
          onBeforeOpen: () => {
            Swal.showLoading()
            this.autoCorrectionDuplicateRecords()
          },
        })
      })
  }

  private showConfirmation(title: string, text: string, confirmText: string, callback: () => void) {
    Swal.fire({
      title, text, icon: 'warning', showCancelButton: true, buttonsStyling: false,
      customClass: { confirmButton: 'btn btn-lg btn-info mr-3', cancelButton: 'btn btn-lg btn-danger' },
      confirmButtonText: confirmText
    }).then((result) => {
      if (result.isConfirmed) callback()
    })
  }
  // #endregion

  // #region API CALLS
  getConfiguracionGlobalPeriodo() {
    this._declaracionService.getGlobalConfiguration().subscribe(
      (response: DjRowResponse<DjConfiguracionGlobalPeriodo>) => {
        this.dj_config = response.result
        this.getDuplicateRecords()
      }, 
      (error) => console.error(error)
    )
  }

  getDuplicateRecords() {
    this.isLoading = true;
    this._declaracionService.getDuplicateRecords().subscribe(
      (response: DjDuplicateRecordResponse) => {
        // Asignar directamente sin ningún procesamiento
        this.dj_duplicate_records = response.result || [];
        this.response_params = response.params;
        this.isLoading = false;
      }, 
      (error) => { 
        this.isLoading = false; 
        console.error('Error al obtener registros:', error);
      }
    )
  }

  updateDuplicateRecord(params: DjOptionDuplicateRecordUpdateRequest) {
    this._declaracionService.updateDuplicateRecord(params).subscribe(
      (response: DjOptionDuplicateRecordUpdateResponse) => {
        this.getConfiguracionGlobalPeriodo()
        this.closeModal()
        
        const result = response.result;
        const nModified = result.updateDuplicate.nModified;
        
        let title = 'Operación Exitosa';
        let htmlMessage = `
          <div class="text-center">
            <i class="fas fa-check-circle text-success fa-2x mb-3"></i>
            <p class="mb-3 text-dark">Se ha agrupado el registro correctamente</p>
            <div class="bg-light rounded py-2 mb-2">
              <span class="text-muted">Registros actualizados: </span>
              <strong class="text-success h6">${nModified.toLocaleString()}</strong>
            </div>
            <div class="bg-light rounded py-2 mb-2">
              <span class="text-muted">RUT: </span>
              <strong class="text-primary">${result.saved_rut_rule.rut}</strong>
            </div>
            <small class="text-muted">Regla aplicada exitosamente</small>
          </div>
        `;

        Swal.fire({
          title: title,
          html: htmlMessage,
          confirmButtonText: 'Entendido',
          buttonsStyling: false,
          width: '400px',
          customClass: {
            confirmButton: 'btn btn-success btn-sm px-4'
          }
        });
      }, 
      (error) => console.error(error)
    )
  }
  
  autoCorrectionDuplicateRecords() {
    this._declaracionService.autoCorrectionDuplicateRecords().subscribe(
      (response: DjAutoCorrectionDuplicateRecordResponse) => {
        this.getConfiguracionGlobalPeriodo()
        
        const result = response.result;
        let htmlMessage = '';
        let title = 'Información';

        // Si el proceso fue exitoso
        if (result.success) {
          // Si se modificaron registros
          if (result.nModified > 0) {
            title = 'Autocorrección Exitosa';
            htmlMessage = `
              <div class="text-center">
                <i class="fas fa-check-circle text-success fa-2x mb-3"></i>
                <div class="row mx-0">
                  <div class="col-6 px-1 mb-2">
                    <div class="bg-light rounded py-2 text-center">
                      <div class="text-primary font-weight-bold h5 mb-1">${result.nMatched.toLocaleString()}</div>
                      <small class="text-muted">Analizados</small>
                    </div>
                  </div>
                  <div class="col-6 px-1 mb-2">
                    <div class="bg-light rounded py-2 text-center">
                      <div class="text-success font-weight-bold h5 mb-1">${result.nModified.toLocaleString()}</div>
                      <small class="text-muted">Corregidos</small>
                    </div>
                  </div>
                  <div class="col-6 px-1">
                    <div class="bg-light rounded py-2 text-center">
                      <div class="text-info font-weight-bold h5 mb-1">${result.operacionesAgregadas.toLocaleString()}</div>
                      <small class="text-muted">Operaciones</small>
                    </div>
                  </div>
                  <div class="col-6 px-1">
                    <div class="bg-light rounded py-2 text-center">
                      <div class="text-warning font-weight-bold h5 mb-1">${result.reglasAplicadas.toLocaleString()}</div>
                      <small class="text-muted">Reglas</small>
                    </div>
                  </div>
                </div>
              </div>
            `;
          } 
          // Si NO se modificaron registros pero el proceso fue exitoso
          else {
            title = 'Intervención Manual Requerida';
            htmlMessage = `
              <div class="text-center">
                <i class="fas fa-hand-pointer text-warning fa-2x mb-3"></i>
                <p class="mb-3 text-dark">Debe elegir manualmente las opciones</p>
                <div class="bg-light rounded py-2 mb-2">
                  <span class="text-muted">Registros: </span>
                  <strong class="text-primary h6">${result.nMatched.toLocaleString()}</strong>
                </div>
                <small class="text-muted">No se encontraron reglas aplicables</small>
              </div>
            `;
          }
        } 
        // Si el proceso falló (result.success === false)
        else {
          // Verificar si es el caso específico de "No se encontraron reglas"
          if (result.message && result.message.includes('No se encontraron reglas de corrección aplicables')) {
            title = 'Intervención Manual Requerida';
            htmlMessage = `
              <div class="text-center">
                <i class="fas fa-hand-pointer text-warning fa-2x mb-3"></i>
                <p class="mb-3 text-dark">Debe elegir manualmente las opciones</p>
                <div class="bg-light rounded py-2 mb-2">
                  <span class="text-muted">Registros pendientes: </span>
                  <strong class="text-warning h6">${this.dj_duplicate_records?.length || 0}</strong>
                </div>
                <small class="text-muted">No se encontraron reglas aplicables</small>
              </div>
            `;
          } else {
            // Otros errores del sistema
            title = 'Error en Proceso';
            htmlMessage = `
              <div class="text-center">
                <i class="fas fa-exclamation-triangle text-danger fa-2x mb-3"></i>
                <p class="mb-2 text-dark">Error del sistema</p>
                <div class="bg-light rounded py-2 mb-2">
                  <small class="text-dark">${result.message || 'Error desconocido'}</small>
                </div>
                <small class="text-muted">Contacte al administrador</small>
              </div>
            `;
          }
        }

        Swal.fire({
          title: title,
          html: htmlMessage,
          confirmButtonText: 'Entendido',
          buttonsStyling: false,
          width: '400px',
          customClass: {
            confirmButton: 'btn btn-primary btn-sm px-4'
          }
        });
      },
      (error) => {
        Swal.fire({
          title: 'Error de Conexión',
          html: `
            <div class="text-center">
              <i class="fas fa-wifi text-danger fa-2x mb-3"></i>
              <p class="mb-2 text-dark">No se pudo conectar</p>
              <small class="text-muted">Verifique su conexión e inténtelo nuevamente</small>
            </div>
          `,
          confirmButtonText: 'Cerrar',
          buttonsStyling: false,
          width: '320px',
          customClass: {
            confirmButton: 'btn btn-danger btn-sm px-4'
          }
        });
        console.log(error);
      }
    )
  }
  // #endregion

  // #region MÉTODOS DE NAVEGACIÓN
  volverADeclaraciones() {
    this.volverADeclaracion.emit();
  }
  // #endregion

  ngOnDestroy() {
    // Limpiar suscripción para evitar memory leaks
    if (this.entidadSelSubscription) {
      this.entidadSelSubscription.unsubscribe();
    }
  }
}
